import { Clock3, Gauge, Sigma } from "lucide-react";
import { useMemo } from "react";
import { NumericInput } from "../../components/NumericInput";
import { copy } from "../../data/i18n";
import { compactDecimalsFor, decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { formatNumber } from "../../utils/numberFormat";
import { calculateTimeSpeed } from "./formulas";
import type { TimeSpeedInputs, TimeSpeedMode } from "./types";

type TimeSpeedCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  mode: TimeSpeedMode;
  inputs: TimeSpeedInputs;
  inputText: Record<keyof TimeSpeedInputs, string>;
  onModeChange: (mode: TimeSpeedMode, calculatedKey?: keyof TimeSpeedInputs, value?: number) => void;
  onInputTextChange: (key: keyof TimeSpeedInputs, value: string) => void;
};

function displayLengthCm(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value / 2.54;
}

export function TimeSpeedCalculator({
  language,
  unitSystem,
  mode,
  inputs,
  inputText,
  onModeChange,
  onInputTextChange
}: TimeSpeedCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";
  const result = useMemo(() => calculateTimeSpeed(mode, inputs), [mode, inputs]);
  const resultMetric = mode === "travelSpeed" ? "travelSpeed" : "time";
  const resultValue = mode === "travelSpeed" ? toDisplayValue("travelSpeed", result.travelSpeed, unitSystem) : result.duration;
  const resultUnit = mode === "travelSpeed" ? unitFor("travelSpeed", unitSystem) : "s";
  const resultDecimals = mode === "travelSpeed" ? decimalsFor("travelSpeed", unitSystem) : 1;
  const lengthUnit = unitSystem === "metric" ? "cm" : "in";

  function changeMode(nextMode: TimeSpeedMode) {
    const currentResult = calculateTimeSpeed(mode, inputs);
    onModeChange(nextMode, mode === "travelSpeed" ? "travelSpeed" : "duration", mode === "travelSpeed" ? currentResult.travelSpeed : currentResult.duration);
  }

  return (
    <section className="calculator-panel" aria-label={t.modules.timeSpeed}>
      <div className="calculator-heading">
        <div>
          <h2>{mode === "travelSpeed" ? t.timeSpeed.calculateSpeed : t.timeSpeed.calculateDuration}</h2>
          <span>{t.modules.timeSpeedDescription}</span>
        </div>
      </div>

      <div className="target-section">
        <div className="section-label">{t.targetSelection}</div>
        <div className="mode-selector geometry-mode-selector">
          <button type="button" className={mode === "travelSpeed" ? "mode-button is-active" : "mode-button"} onClick={() => changeMode("travelSpeed")}>
            <span>{t.timeSpeed.calculateSpeed}</span>
            <small>{unitFor("travelSpeed", unitSystem)}</small>
          </button>
          <button type="button" className={mode === "duration" ? "mode-button is-active" : "mode-button"} onClick={() => changeMode("duration")}>
            <span>{t.timeSpeed.calculateDuration}</span>
            <small>s</small>
          </button>
        </div>
      </div>

      <div className="calculator-body conversion-body">
        <div className="parameter-section">
          <div className="section-label">{t.inputParameters}</div>
          <div className="input-grid">
            <NumericInput
              label={t.timeSpeed.weldLength}
              shortLabel={t.timeSpeed.lengthShort}
              unit={lengthUnit}
              value={displayLengthCm(inputs.weldLength, unitSystem)}
              textValue={inputText.weldLength}
              decimals={unitSystem === "metric" ? 1 : 2}
              locale={locale}
              onChange={(value) => onInputTextChange("weldLength", value)}
            />
            <NumericInput
              label={t.timeSpeed.duration}
              shortLabel={t.timeSpeed.durationShort}
              unit="s"
              value={mode === "duration" ? result.duration : inputs.duration}
              textValue={inputText.duration}
              decimals={1}
              locale={locale}
              disabled={mode === "duration"}
              onChange={(value) => onInputTextChange("duration", value)}
            />
            <NumericInput
              label={t.inputs.travelSpeed}
              shortLabel="vs"
              unit={unitFor("travelSpeed", unitSystem)}
              value={toDisplayValue("travelSpeed", mode === "travelSpeed" ? result.travelSpeed : inputs.travelSpeed, unitSystem)}
              textValue={inputText.travelSpeed}
              decimals={decimalsFor("travelSpeed", unitSystem)}
              locale={locale}
              disabled={mode === "travelSpeed"}
              onChange={(value) => onInputTextChange("travelSpeed", value)}
            />
          </div>
        </div>

        <aside className="result-panel conversion-result" aria-live="polite">
          <div className="result-header">
            <span>
              <Clock3 size={16} /> {t.result}
            </span>
          </div>
          <p className="result-label">{mode === "travelSpeed" ? t.timeSpeed.travelSpeedResult : t.timeSpeed.durationResult}</p>
          <div className="result-value">
            {formatNumber(resultValue, resultDecimals, locale)}
            <span>{resultUnit}</span>
          </div>

          <div className="formula-box">
            <span>
              <Sigma size={15} /> {t.formula}
            </span>
            <code>{mode === "travelSpeed" ? "vs = length / duration * 60" : "duration = length / vs * 60"}</code>
          </div>

          <div className="detail-grid">
            <div>
              <span>{t.timeSpeed.durationMinutes}</span>
              <strong>{formatNumber(result.durationMinutes, 2, locale)} min</strong>
            </div>
            <div>
              <span>{t.timeSpeed.lengthPerSecond}</span>
              <strong>{formatNumber(displayLengthCm(result.lengthPerSecond, unitSystem), unitSystem === "metric" ? 2 : 3, locale)} {lengthUnit}/s</strong>
            </div>
            <div>
              <span>{t.inputs.travelSpeed}</span>
              <strong>{formatNumber(toDisplayValue("travelSpeed", result.travelSpeed, unitSystem), compactDecimalsFor("travelSpeed", unitSystem, toDisplayValue("travelSpeed", result.travelSpeed, unitSystem)), locale)} {unitFor("travelSpeed", unitSystem)}</strong>
            </div>
            <div>
              <span>{t.timeSpeed.weldLength}</span>
              <strong>{formatNumber(displayLengthCm(inputs.weldLength, unitSystem), unitSystem === "metric" ? 1 : 2, locale)} {lengthUnit}</strong>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
